/**
 * Coach Steve sub-agent barrel.
 *
 * Importing this module registers every built-in sub-agent with the
 * shared registry. Order is preserved so router prompt + dispatcher
 * iteration stay deterministic.
 */

import { registerAgent } from './registry';
import { practiceAgent } from './practiceAgent';
import { gamePlanAgent } from './gamePlanAgent';
import { gameAgent } from './gameAgent';
import { formationAgent } from './formationAgent';
import { depthChartAgent } from './depthChartAgent';
import { rosterAgent } from './rosterAgent';
import { statsAgent } from './statsAgent';
import { attendanceAgent } from './attendanceAgent';
import { playbookAgent } from './playbookAgent';
import { helpAgent } from './helpAgent';

let registered = false;

/**
 * Idempotent registration. Imported for side effect via `agents/index`,
 * but exposed as a function so unit tests can rebuild the registry.
 */
export function registerAllAgents(): void {
  if (registered) return;
  registerAgent(practiceAgent);
  registerAgent(gamePlanAgent);
  registerAgent(gameAgent);
  registerAgent(formationAgent);
  registerAgent(depthChartAgent);
  registerAgent(rosterAgent);
  registerAgent(statsAgent);
  registerAgent(attendanceAgent);
  registerAgent(playbookAgent);
  registerAgent(helpAgent);
  registered = true;
}

registerAllAgents();

export * from './types';
export { listAgents, getAgent } from './registry';
export {
  routeUserMessage,
  isRouterEnabled,
  isToolCallingRouterEnabled,
  buildRouterPrompt,
} from './router';
export {
  RouterIntentSchema,
  RouterSubAgent,
  ROUTER_CONFIDENCE_THRESHOLD,
} from './routerSchema';
export type { ValidatedRouterIntent } from './routerSchema';
export { logRouted, getRouterLog, isRouterDebugEnabled } from './routerLogger';

// Re-export individual agents so callers can reach them without going
// through the registry (e.g. for direct invocation in CoachSteve.tsx).
export { practiceAgent } from './practiceAgent';
export { gamePlanAgent } from './gamePlanAgent';
export { gameAgent, startCreateGameFlow } from './gameAgent';
export { formationAgent } from './formationAgent';
export { depthChartAgent } from './depthChartAgent';
export { continuePendingGameLineupPick } from './depthChartGameScoped';
export {
  rosterAgent,
  runAddPlayer,
  runInjuryStatusFlow,
  continuePendingPlayerCreate,
  matchSetInjury,
} from './rosterAgent';
export { statsAgent, isStatsLookupIntent } from './statsAgent';
export { attendanceAgent, isAttendanceLookupIntent } from './attendanceAgent';
export { playbookAgent } from './playbookAgent';
export { helpAgent, runHelpAgent } from './helpAgent';

// Pure helpers
export {
  isCreatePracticeIntent,
  isAddDrillToPracticeIntent,
  supersedesPendingPlayerCreateWithPracticeRequest,
  isCreateGamePlanIntent,
  isCreateGameIntent,
  isCreateFormationIntent,
  isCreatePlayerIntent,
  isAssignPlayerToFormationIntent,
  isGameLineupCoachIntent,
  isSetInjuryIntent,
} from './intents';
export {
  extractPlayerName,
  extractJerseyNumber,
  extractIsSub,
  extractInjuryStatusFromText,
  extractAssignFormationFromMessage,
  extractAssignPlayerPositionOnly,
  extractPracticeFocusFromMessage,
  extractPracticeActivityBrief,
  extractRequestedDrillPhrase,
  parsePracticeTargetDateFromMessage,
  parseGameDetailsFromMessage,
  getTodayDateStr,
  isAffirmativeReply,
  isNegativeReply,
  normalizePositionKey,
  POSITION_ALIASES,
  getTomorrowDateStr,
} from './parsers';
export {
  extractPositionGroupsFromText,
  inferRoleTypesFromGroups,
} from './positionGroups';
export {
  fetchRosterForInjury,
  findPlayersForInjury,
  playerRefToIdentifier,
  extractPlayerFromInjuryMessage,
} from './playerLookup';
export type {
  PlayerIdentifier,
  RosterPlayerForInjury,
} from './playerLookup';

// Practice agent helpers re-exported so the chat handler can call them
// without importing from the agent file directly.
export {
  createPlaceholderPractice,
  runPlanGeneration,
  startPracticePlaceholderFlow,
  continuePendingPracticeFocus,
  continuePendingAddDrillFollowUp,
  handlePracticeCreated,
} from './practiceAgent';
